/**
 * §B-2 carrier integration drawer. Replaces the old checkbox-driven 5-step wizard
 * with a connect-then-manage flow modelled on first-class integration settings
 * (Stripe / Vercel / GitHub):
 *   • New carrier → a single SETUP form (carrier + scope + mode + credentials).
 *   • Connected carrier → a STATUS view: the live state (Active / Connected /
 *     Not connected), the §7 test gate, and explicit actions — Test connection,
 *     Generate test label, Enable / Disable, Replace credentials, Edit settings,
 *     Disconnect (clear creds, keep the row) and Delete.
 *
 * Credentials are write-only — never shown back, only "set / not set" + key names.
 * Editing is ALWAYS explicit: opening a connected carrier shows status, never an
 * editable form; you pick Replace credentials / Edit settings to change anything
 * (so there is no "Replace credentials" checkbox sitting in a form). Rotating
 * credentials or changing account details resets the gate server-side (§7), so the
 * status view simply re-reflects "needs a test-label" afterwards.
 */
import { useState } from "react";
import { ApiError, api, type AdminCarrierConfig, type CarrierCredentialField, type CarrierConfigUpdateInput } from "../../../lib/api.js";
import { humanizeCode } from "../../../lib/format.js";
import { formatAge } from "../../../lib/age.js";
import { Drawer, Field, Input, Select, Button, useToast, Icon } from "../../../components/ui/index.js";

interface StoreOpt {
  id: string;
  label: string;
}

type Toast = ReturnType<typeof useToast>["toast"];

const FALLBACK_FIELDS: CarrierCredentialField[] = [{ key: "apiKey", label: "API key", required: true, kind: "secret" }];

/** Surface a write failure with the most specific message we can. */
function reportError(toast: Toast, e: unknown) {
  if (e instanceof ApiError && e.status === 409) {
    const code = (e.detail as { code?: string } | undefined)?.code;
    toast(code === "CONFLICT" ? "A config for that carrier + store already exists." : "This changed since you opened it — reopen it.", "danger");
  } else if (e instanceof ApiError && e.status === 422) {
    toast((e.detail as { error?: string } | undefined)?.error ?? "Generate a passing test-label before enabling.", "danger");
  } else if (e instanceof ApiError && e.status === 400) {
    toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid config.", "danger");
  } else if (e instanceof ApiError && e.status === 403) {
    toast("Admin role required.", "danger");
  } else {
    toast("Something went wrong.", "danger");
  }
}

/** The carrier's live integration state — the single scannable status. */
type IntKey = "disconnected" | "incomplete" | "paused" | "active";
function integrationState(c: AdminCarrierConfig): { key: IntKey; label: string; tone: "ready" | "hold" | "cancelled"; hint: string } {
  if (c.credentialStatus !== "present")
    return { key: "disconnected", label: "Not connected", tone: "cancelled", hint: "No credentials stored. Enter credentials to connect this carrier." };
  if (c.productionReady) return { key: "active", label: "Active", tone: "ready", hint: "Live — creating production labels." };
  if (c.testLabelOk) return { key: "paused", label: "Connected · paused", tone: "cancelled", hint: "Tested and ready, but not enabled for production." };
  return { key: "incomplete", label: "Connected · setup incomplete", tone: "hold", hint: "Credentials stored. Generate a passing test label to go live." };
}

/** Canonical accountsJson form (mirrors the worker) so we can detect a real change. */
function canonicalAccounts(a: unknown): string {
  if (!a || typeof a !== "object" || Array.isArray(a)) return "{}";
  const o = a as Record<string, unknown>;
  return JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k] == null ? null : String(o[k])]));
}

/** Merge edited account-field values over the stored blob, preserving unknown keys. */
function buildAccountsJson(
  accountFields: CarrierCredentialField[],
  vals: Record<string, string>,
  existing: Record<string, unknown>,
): Record<string, unknown> | null {
  const known = new Set(accountFields.map((f) => f.key));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(existing)) if (!known.has(k) && v != null && String(v).trim() !== "") out[k] = v;
  for (const f of accountFields) {
    const v = vals[f.key]?.trim();
    if (v) out[f.key] = v;
  }
  return Object.keys(out).length ? out : null;
}

type View = "setup" | "status" | "replace" | "settings";

export function CarrierConfigEditor({
  config: initial,
  carrierCodes,
  credentialFields,
  stores,
  onClose,
  onChanged,
}: {
  config: AdminCarrierConfig | null;
  carrierCodes: string[];
  credentialFields: Record<string, CarrierCredentialField[]>;
  stores: StoreOpt[];
  onClose: () => void;
  /** Reload the list behind the drawer after any mutation (drawer stays open). */
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [config, setConfig] = useState<AdminCarrierConfig | null>(initial);
  const [view, setView] = useState<View>(initial ? "status" : "setup");

  const carrierCode = config?.carrierCode ?? carrierCodes[0] ?? "";
  const fields = credentialFields[carrierCode] ?? FALLBACK_FIELDS;
  const scopeLabel = (id: string | null) => (id ? (stores.find((s) => s.id === id)?.label ?? id) : "All stores");

  function applied(next: AdminCarrierConfig) {
    setConfig(next);
    setView("status");
    onChanged();
  }

  const title = config ? humanizeCode(config.carrierCode) : "Connect a carrier";
  const meta = config ? <span className="muted">{scopeLabel(config.storeId)} · {humanizeCode(config.defaultMode)}</span> : <span className="muted">Carrier · credentials · go live</span>;

  return (
    <Drawer onClose={onClose} ariaLabel={config ? `${humanizeCode(config.carrierCode)} integration` : "Connect a carrier"} title={title} meta={meta}>
      {view === "setup" && (
        <SetupForm
          carrierCodes={carrierCodes}
          credentialFields={credentialFields}
          stores={stores}
          toast={toast}
          onCancel={onClose}
          onCreated={applied}
        />
      )}

      {config && view === "status" && (
        <StatusView
          config={config}
          fields={fields}
          scopeLabel={scopeLabel(config.storeId)}
          toast={toast}
          onConfig={(c) => {
            setConfig(c);
            onChanged();
          }}
          onReplace={() => setView("replace")}
          onSettings={() => setView("settings")}
          onClose={onClose}
          onChanged={onChanged}
        />
      )}

      {config && view === "replace" && (
        <CredentialsForm
          mode={config.credentialStatus === "present" ? "replace" : "reconnect"}
          config={config}
          fields={fields}
          toast={toast}
          onCancel={() => setView("status")}
          onSaved={applied}
        />
      )}

      {config && view === "settings" && (
        <SettingsForm config={config} fields={fields} toast={toast} onCancel={() => setView("status")} onSaved={applied} />
      )}
    </Drawer>
  );
}

/* ----------------------------- Setup (new) ------------------------------ */

function SetupForm({
  carrierCodes,
  credentialFields,
  stores,
  toast,
  onCancel,
  onCreated,
}: {
  carrierCodes: string[];
  credentialFields: Record<string, CarrierCredentialField[]>;
  stores: StoreOpt[];
  toast: Toast;
  onCancel: () => void;
  onCreated: (c: AdminCarrierConfig) => void;
}) {
  const [carrierCode, setCarrierCode] = useState(carrierCodes[0] ?? "");
  const [scope, setScope] = useState("");
  const [defaultMode, setDefaultMode] = useState("PACK_FIRST");
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [accountVals, setAccountVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const fields = credentialFields[carrierCode] ?? FALLBACK_FIELDS;
  const secretFields = fields.filter((f) => f.kind === "secret");
  const accountFields = fields.filter((f) => f.kind === "account" || f.kind === "option");

  async function connect() {
    for (const f of secretFields) if (f.required && !creds[f.key]?.trim()) return toast(`${f.label} is required.`, "danger");
    for (const f of accountFields) if (f.required && !accountVals[f.key]?.trim()) return toast(`${f.label} is required.`, "danger");
    setBusy(true);
    try {
      const res = await api.admin.createCarrierConfig({
        carrierCode,
        storeId: scope || null,
        enabled: false,
        defaultMode,
        credentials: creds,
        accountsJson: buildAccountsJson(accountFields, accountVals, {}),
        rateLimitJson: null,
      });
      toast("Carrier connected — now run the tests to go live.", "success");
      onCreated(res.config);
    } catch (e) {
      reportError(toast, e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rule-form">
      <div className="rule-form__row2">
        <Field label="Carrier">
          <Select ariaLabel="Carrier" value={carrierCode} onChange={setCarrierCode} options={carrierCodes.map((c) => ({ value: c, label: humanizeCode(c) }))} />
        </Field>
        <Field label="Scope">
          <Select
            ariaLabel="Scope"
            value={scope}
            onChange={setScope}
            options={[{ value: "", label: "All stores (global)" }, ...stores.map((s) => ({ value: s.id, label: s.label }))]}
          />
        </Field>
      </div>
      <Field label="Default mode" hint="Pack-first scans then labels; label-first prints on release.">
        <Select ariaLabel="Default mode" value={defaultMode} onChange={setDefaultMode} options={[{ value: "PACK_FIRST", label: "Pack first" }, { value: "LABEL_FIRST", label: "Label first" }]} />
      </Field>

      <fieldset className="secret-group">
        <legend>Credentials</legend>
        <p className="field-hint">Encrypted at rest and never shown back — only “set / not set”.</p>
        {secretFields.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint}>
            <Input type="password" value={creds[f.key] ?? ""} onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))} placeholder={f.required ? "required" : "optional"} autoComplete="off" />
          </Field>
        ))}
      </fieldset>

      {accountFields.length > 0 && (
        <fieldset className="secret-group">
          <legend>Account &amp; options</legend>
          {accountFields.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint}>
              <Input value={accountVals[f.key] ?? ""} onChange={(e) => setAccountVals((a) => ({ ...a, [f.key]: e.target.value }))} placeholder={f.required ? "required" : "optional"} autoComplete="off" />
            </Field>
          ))}
        </fieldset>
      )}

      <div className="rule-form__actions">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" icon="check" onClick={() => void connect()} disabled={busy || !carrierCode}>{busy ? "Connecting…" : "Connect"}</Button>
      </div>
    </div>
  );
}

/* ------------------------------- Status -------------------------------- */

function StatusView({
  config,
  fields,
  scopeLabel,
  toast,
  onConfig,
  onReplace,
  onSettings,
  onClose,
  onChanged,
}: {
  config: AdminCarrierConfig;
  fields: CarrierCredentialField[];
  scopeLabel: string;
  toast: Toast;
  onConfig: (c: AdminCarrierConfig) => void;
  onReplace: () => void;
  onSettings: () => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const now = Date.now();
  const state = integrationState(config);
  const connected = config.credentialStatus === "present";
  const accountFields = fields.filter((f) => f.kind === "account" || f.kind === "option");
  const accounts = (config.accountsJson ?? {}) as Record<string, unknown>;

  async function runTest(kind: "connection" | "label") {
    setBusy(true);
    setResult(null);
    try {
      const res = kind === "connection" ? await api.admin.testCarrierConnection(config.id) : await api.admin.testCarrierLabel(config.id);
      if (res.config) onConfig(res.config);
      setResult({ ok: res.ok, text: res.detail });
    } catch {
      setResult({ ok: false, text: "Couldn’t run the test." });
    } finally {
      setBusy(false);
    }
  }

  async function setEnabled(next: boolean) {
    setBusy(true);
    try {
      const res = await api.admin.updateCarrierConfig(config.id, { enabled: next, defaultMode: config.defaultMode }, config.updatedAt);
      onConfig(res.config);
      toast(res.config.enabled ? "Enabled for production." : "Disabled.", "success");
      setResult(null);
    } catch (e) {
      reportError(toast, e);
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm(`Disconnect ${humanizeCode(config.carrierCode)}? Credentials are cleared and it stops creating labels. The configuration (scope, mode, account numbers) is kept — re-enter credentials to reconnect.`)) return;
    setBusy(true);
    try {
      const res = await api.admin.disconnectCarrier(config.id, config.updatedAt);
      onConfig(res.config);
      toast("Disconnected — credentials cleared.", "success");
      setResult(null);
    } catch (e) {
      reportError(toast, e);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete the ${humanizeCode(config.carrierCode)} configuration (${scopeLabel}) entirely? Routing rules pointing at it will fail to resolve an adapter.`)) return;
    setBusy(true);
    try {
      await api.admin.deleteCarrierConfig(config.id);
      toast("Configuration deleted.", "success");
      onChanged();
      onClose();
    } catch (e) {
      reportError(toast, e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rule-form">
      <div className="int-status">
        <span className={`chip chip--${state.tone} int-status__chip`}><span className="chip__dot" aria-hidden="true" />{state.label}</span>
        <p className="int-status__hint">{state.hint}</p>
      </div>

      <dl className="int-facts">
        <div className="int-facts__row">
          <dt>Credentials</dt>
          <dd>{connected ? <>set · <span className="muted">{config.credentialKeys.join(", ") || "stored"}</span></> : <span className="muted">not set</span>}</dd>
        </div>
        {accountFields.map((f) => {
          const v = accounts[f.key];
          return (
            <div className="int-facts__row" key={f.key}>
              <dt>{f.label}</dt>
              <dd>{v != null && String(v).trim() !== "" ? String(v) : <span className="muted">—</span>}</dd>
            </div>
          );
        })}
        <div className="int-facts__row">
          <dt>Default mode</dt>
          <dd>{humanizeCode(config.defaultMode)}</dd>
        </div>
      </dl>

      {connected && (
        <dl className="int-facts">
          <div className="int-facts__row">
            <dt>Connection test</dt>
            <dd className={config.testConnectionOk ? "int-ok" : "muted"}>
              {config.testConnectionOk ? <><Icon name="check" size={13} /> passed{config.testConnectionAt ? ` · ${formatAge(config.testConnectionAt, now)}` : ""}</> : "not checked"}
            </dd>
          </div>
          <div className="int-facts__row">
            <dt>Test label</dt>
            <dd className={config.testLabelOk ? "int-ok" : "muted"}>
              {config.testLabelOk ? <><Icon name="check" size={13} /> passed{config.testLabelAt ? ` · ${formatAge(config.testLabelAt, now)}` : ""}</> : "not passed"}
            </dd>
          </div>
          {config.testDetail && <p className="field-hint" title={config.testDetail}>{config.testDetail}</p>}
        </dl>
      )}

      {result && (
        <p className={"wizard-action__result " + (result.ok ? "wizard-action__result--ok" : "wizard-action__result--err")}>
          <Icon name={result.ok ? "check" : "alert"} size={14} /> {result.text}
        </p>
      )}

      {/* Primary gate action */}
      {!connected ? (
        <Button variant="primary" icon="rotate" onClick={onReplace} disabled={busy} block>Enter credentials to reconnect</Button>
      ) : state.key === "active" ? (
        <Button variant="secondary" icon="dot" onClick={() => void setEnabled(false)} disabled={busy} block>{busy ? "Working…" : "Disable (stop production labels)"}</Button>
      ) : config.testLabelOk ? (
        <Button variant="primary" icon="check" onClick={() => void setEnabled(true)} disabled={busy} block>{busy ? "Working…" : "Enable for production"}</Button>
      ) : (
        <Button variant="primary" icon="bolt" onClick={() => void runTest("label")} disabled={busy} block>{busy ? "Running…" : "Generate test label to go live"}</Button>
      )}

      {connected && (
        <>
          <div className="int-actions">
            <Button variant="secondary" size="sm" icon="globe" onClick={() => void runTest("connection")} disabled={busy}>Test connection</Button>
            <Button variant="secondary" size="sm" icon="bolt" onClick={() => void runTest("label")} disabled={busy}>Generate test label</Button>
          </div>
          <div className="int-actions">
            <Button variant="secondary" size="sm" icon="rotate" onClick={onReplace} disabled={busy}>Replace credentials</Button>
            <Button variant="secondary" size="sm" icon="sliders" onClick={onSettings} disabled={busy}>Edit settings</Button>
          </div>
        </>
      )}

      <div className="int-actions int-actions--danger">
        {connected && <Button variant="danger" size="sm" icon="logout" onClick={() => void disconnect()} disabled={busy}>Disconnect</Button>}
        <Button variant="ghost" size="sm" icon="x" onClick={() => void remove()} disabled={busy}>Delete configuration</Button>
      </div>
    </div>
  );
}

/* ------------------- Replace credentials / Reconnect ------------------- */

function CredentialsForm({
  mode,
  config,
  fields,
  toast,
  onCancel,
  onSaved,
}: {
  mode: "replace" | "reconnect";
  config: AdminCarrierConfig;
  fields: CarrierCredentialField[];
  toast: Toast;
  onCancel: () => void;
  onSaved: (c: AdminCarrierConfig) => void;
}) {
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const secretFields = fields.filter((f) => f.kind === "secret");

  async function save() {
    for (const f of secretFields) if (f.required && !creds[f.key]?.trim()) return toast(`${f.label} is required.`, "danger");
    setBusy(true);
    try {
      // Rotating credentials always resets the §7 gate server-side → lands disabled.
      const input: CarrierConfigUpdateInput = { enabled: false, defaultMode: config.defaultMode, credentials: creds };
      const res = await api.admin.updateCarrierConfig(config.id, input, config.updatedAt);
      toast(mode === "reconnect" ? "Reconnected — re-test to go live." : "Credentials replaced — re-test to go live.", "success");
      onSaved(res.config);
    } catch (e) {
      reportError(toast, e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rule-form">
      <fieldset className="secret-group">
        <legend>{mode === "reconnect" ? "Enter credentials" : "Replace credentials"}</legend>
        <p className="field-hint">
          {mode === "reconnect"
            ? "Stored credentials were cleared. Enter them to reconnect."
            : config.credentialKeys.length > 0
              ? `Currently stored: ${config.credentialKeys.join(", ")}. New values overwrite them and require a fresh test-label before going live.`
              : "New credentials require a fresh test-label before going live."}
        </p>
        {secretFields.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint}>
            <Input type="password" value={creds[f.key] ?? ""} onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))} placeholder={f.required ? "required" : "optional"} autoComplete="off" />
          </Field>
        ))}
      </fieldset>
      <div className="rule-form__actions">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" icon="check" onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save credentials"}</Button>
      </div>
    </div>
  );
}

/* --------------------------- Edit settings ----------------------------- */

function SettingsForm({
  config,
  fields,
  toast,
  onCancel,
  onSaved,
}: {
  config: AdminCarrierConfig;
  fields: CarrierCredentialField[];
  toast: Toast;
  onCancel: () => void;
  onSaved: (c: AdminCarrierConfig) => void;
}) {
  const accountFields = fields.filter((f) => f.kind === "account" || f.kind === "option");
  const initialAccounts = (config.accountsJson ?? {}) as Record<string, unknown>;
  const [defaultMode, setDefaultMode] = useState(config.defaultMode);
  const [accountVals, setAccountVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(accountFields.map((f) => [f.key, initialAccounts[f.key] == null ? "" : String(initialAccounts[f.key])])),
  );
  const [busy, setBusy] = useState(false);

  async function save() {
    for (const f of accountFields) if (f.required && !accountVals[f.key]?.trim()) return toast(`${f.label} is required.`, "danger");
    const accountsJson = buildAccountsJson(accountFields, accountVals, initialAccounts);
    // Changing account/option fields resets the gate (§7) just like rotating creds,
    // so we can only keep the carrier enabled when the accounts are unchanged.
    const accountsChanged = canonicalAccounts(accountsJson) !== canonicalAccounts(config.accountsJson);
    setBusy(true);
    try {
      const res = await api.admin.updateCarrierConfig(
        config.id,
        { enabled: config.enabled && !accountsChanged, defaultMode, accountsJson },
        config.updatedAt,
      );
      toast(accountsChanged ? "Settings saved — account details changed, re-test to go live." : "Settings saved.", "success");
      onSaved(res.config);
    } catch (e) {
      reportError(toast, e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rule-form">
      <Field label="Default mode" hint="Pack-first scans then labels; label-first prints on release.">
        <Select ariaLabel="Default mode" value={defaultMode} onChange={setDefaultMode} options={[{ value: "PACK_FIRST", label: "Pack first" }, { value: "LABEL_FIRST", label: "Label first" }]} />
      </Field>
      {accountFields.length > 0 && (
        <fieldset className="secret-group">
          <legend>Account &amp; options</legend>
          <p className="field-hint">Changing account numbers or the endpoint resets the test gate — you’ll re-test before going live.</p>
          {accountFields.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint}>
              <Input value={accountVals[f.key] ?? ""} onChange={(e) => setAccountVals((a) => ({ ...a, [f.key]: e.target.value }))} placeholder={f.required ? "required" : "optional"} autoComplete="off" />
            </Field>
          ))}
        </fieldset>
      )}
      <div className="rule-form__actions">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" icon="check" onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save settings"}</Button>
      </div>
    </div>
  );
}
